// ============================================================================
// MyAccountView.tsx — ЛИЧНЫЙ СЧЁТ пользователя (единый вид, SSoT).
//
// Один и тот же экран используется в двух местах:
//   • «Мой счёт» в меню пользователя (MyAccountDialog — окно);
//   • вкладка «Счета/оплаты» приложения «Кабинет» (CabinetBillingTab).
// Логика не дублируется: баланс/холды/история — GetMyBillingAccount
// (sdk.billing), пополнение — sdk.payments (провайдер + сумма → платёж).
// Выключенный биллинг — явное состояние, а не ошибка интерфейса.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { createLogger } from '@/lib/logger';

const log = createLogger('MyAccount');

const KIND_LABELS: Record<string, string> = {
  deposit: 'Пополнение', debit: 'Списание', hold: 'Холд', capture: 'Подтверждение',
  release: 'Снятие холда', transfer: 'Перевод', refund: 'Возврат',
};
const STATE_LABELS: Record<string, string> = {
  pending: 'Ожидание', done: 'Выполнено', failed: 'Ошибка', released: 'Снято',
};

const n = (v: number | string | bigint | undefined) => Number(v) || 0;

export function MyAccountView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<any>(null);
  const [balance, setBalance] = useState<any>(null);
  const [holds, setHolds] = useState<any[]>([]);
  const [operations, setOperations] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r: any = await getSDK().billing.getMyBillingAccount({});
      setAccount(r?.account || null);
      setBalance(r?.balance || null);
      setHolds(r?.holds || []);
      setOperations(r?.operations || []);
    } catch (e: any) {
      // Выключенный биллинг — не ошибка интерфейса, а явное состояние.
      const msg = friendlyError(e);
      setError(msg || 'Не удалось загрузить счёт');
      log.error('load my account', { error: String(e?.message || e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const disabled = !!error && error.includes('не включён');

  if (loading) {
    return <div className="p-3 text-xs text-muted-foreground">Загрузка…</div>;
  }

  if (disabled) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        Биллинг не включён в базе. Обратитесь к администратору (Администрирование → Биллинг).
      </div>
    );
  }

  if (error || !balance) {
    return (
      <div className="flex flex-col gap-2 p-3">
        <div className="text-xs text-destructive break-words">{error || 'Счёт недоступен'}</div>
        <Button size="sm" variant="outline" className="self-start" onClick={() => void load()}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Повторить
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3 text-sm">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Wallet className="h-4 w-4 text-primary" /> {account?.name || 'Личный счёт'}
        {balance.currency ? <span className="text-xs text-muted-foreground">· {balance.currency}</span> : null}
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="border rounded-lg p-2">
          <div className="text-[11px] text-muted-foreground">Баланс</div>
          <div className="text-lg tabular-nums">{n(balance.balance)}</div>
        </div>
        <div className="border rounded-lg p-2">
          <div className="text-[11px] text-muted-foreground">В холдах</div>
          <div className="text-lg tabular-nums">{n(balance.holds)}</div>
        </div>
        <div className="border rounded-lg p-2">
          <div className="text-[11px] text-muted-foreground">Доступно</div>
          <div className="text-lg tabular-nums">{n(balance.available)}</div>
        </div>
      </div>

      {holds.length > 0 && (
        <div>
          <div className="text-xs font-medium mb-1">Активные холды</div>
          <table className="w-full text-xs border-collapse">
            <thead className="bg-muted/60"><tr>
              <th className="text-right px-2 py-1">Сумма</th><th className="text-left px-2 py-1">Состояние</th>
              <th className="text-left px-2 py-1">Истекает</th>
            </tr></thead>
            <tbody>
              {holds.map((h) => (
                <tr key={h.id} className="border-t">
                  <td className="px-2 py-1 text-right tabular-nums">{n(h.amount)}</td>
                  <td className="px-2 py-1">{STATE_LABELS[h.state] || h.state}</td>
                  <td className="px-2 py-1">{h.expiresAt || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TopUpBlock onDone={() => void load()} />

      <div>
        <div className="text-xs font-medium mb-1">История операций</div>
        <table className="w-full text-xs border-collapse">
          <thead className="bg-muted/60"><tr>
            <th className="text-left px-2 py-1">Дата</th><th className="text-left px-2 py-1">Вид</th>
            <th className="text-left px-2 py-1">Состояние</th><th className="text-right px-2 py-1">Сумма</th>
          </tr></thead>
          <tbody>
            {operations.map((o) => (
              <tr key={o.id} className="border-t">
                <td className="px-2 py-1 whitespace-nowrap">{o.createdAt}</td>
                <td className="px-2 py-1">{KIND_LABELS[o.kind] || o.kind}</td>
                <td className="px-2 py-1">{STATE_LABELS[o.state] || o.state}</td>
                <td className="px-2 py-1 text-right tabular-nums">{n(o.amount)}</td>
              </tr>
            ))}
            {operations.length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center text-muted-foreground">Операций нет</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TopUpBlock — «Пополнить»: провайдер + сумма → создание платежа. ЮKassa →
// переход по confirmation_url; счёт (manual) → инструкция по реквизитам.
// ---------------------------------------------------------------------------
function TopUpBlock({ onDone }: { onDone: () => void }) {
  const [providers, setProviders] = useState<Array<{ code: string; title: string }>>([]);
  const [provider, setProvider] = useState('');
  const [amount, setAmount] = useState('500');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [providerError, setProviderError] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const res: any = await getSDK().payments.listPaymentProviders({});
        const list = (res?.items || []).map((p: any) => ({ code: p.code, title: p.title }));
        setProviders(list);
        if (list.length > 0) setProvider((cur) => cur || list[0].code);
      } catch (e) {
        setProviderError(friendlyError(e));
        log.error('провайдеры платежей', { error: String(e) });
      }
    })();
  }, []);

  const topUp = async () => {
    const rub = Number(amount);
    if (!provider || !Number.isFinite(rub) || rub <= 0) {
      setMessage('Укажите провайдера и сумму');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const res: any = await getSDK().payments.createPayment({
        provider,
        amount: BigInt(Math.round(rub * 100)),
        currency: 'RUB',
        purpose: 'topup',
        description: 'Пополнение счёта',
      });
      if (res?.confirmationUrl) {
        window.open(res.confirmationUrl, '_blank', 'noopener');
        setMessage('Открыто окно оплаты — завершите платёж и обновите счёт');
      } else {
        setMessage('Счёт на оплату создан: оплатите по реквизитам — зачисление произойдёт автоматически');
      }
      onDone();
    } catch (e) {
      setMessage('Не удалось создать платёж: ' + friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  if (providers.length === 0) {
    if (!providerError) return null;
    return (
      <div className="rounded-md border p-2 mb-3 text-[11px] text-muted-foreground">
        Пополнение недоступно: {providerError}
      </div>
    );
  }

  return (
    <div className="rounded-md border p-2 mb-3 space-y-2">
      <div className="text-xs font-medium">Пополнить счёт</div>
      <div className="flex items-center gap-2">
        <select
          className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          {providers.map((p) => <option key={p.code} value={p.code}>{p.title}</option>)}
        </select>
        <input
          className="w-24 rounded-md border border-border bg-background px-2 py-1 text-xs text-right tabular-nums"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
        />
        <span className="text-xs text-muted-foreground">руб.</span>
        <Button size="sm" onClick={() => void topUp()} disabled={busy}>
          {busy ? '…' : 'Пополнить'}
        </Button>
      </div>
      {message ? <div className="text-[11px] text-muted-foreground">{message}</div> : null}
    </div>
  );
}
